import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Leaf, CalendarCheck, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const NORTHAMPTON_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["MedicalBusiness", "HealthAndBeautyBusiness"],
  "name": "IIY Phytoventra Ltd",
  "alternateName": "Invest In Yourself",
  "url": "https://iiypv.com/northampton-wellness",
  "email": "sales@iiypv.com",
  "image": "https://iiypv.com/og-image.jpg",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "GB"
  },
  "areaServed": ["United Kingdom"],
  "knowsAbout": ["Medical herbalism", "Curcumin supplements", "Natural wellbeing", "BCM-95"],
  "description": "UK wellbeing provider offering clinical-grade botanical supplements and qualified medical herbalist consultations for clients across the UK."
};

export default function NorthamptonWellness() {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="northampton-page">
      <SEO
        title="Herbalist Consultations & Wellbeing Workshops | IIY Phytoventra"
        description="IIY Phytoventra: medical herbalist consultations, wellbeing workshops and clinical-grade curcumin supplements, serving clients across the whole UK."
        keywords="medical herbalist UK, wellbeing workshops, natural health UK, herbalist consultation, botanical nutrition, curcumin supplements UK"
        jsonLd={NORTHAMPTON_JSONLD}
      />

      <section className="bg-gradient-to-br from-emerald-800 to-emerald-950 text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-emerald-200 font-semibold mb-3 uppercase tracking-wide text-sm inline-flex items-center gap-2"><Leaf className="w-4 h-4" /> Herbalist-led wellbeing</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-5 font-heading" data-testid="northampton-h1">Herbalist Support &amp; Botanical Nutrition</h1>
          <p className="text-lg text-emerald-100/90 max-w-2xl">Serving clients across the whole UK with qualified medical herbalist guidance and clinically studied botanical supplements.</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-14 space-y-12">
        <section>
          <h2 className="text-2xl font-bold text-emerald-900 mb-5 font-heading flex items-center gap-2"><Users className="w-6 h-6" /> Community initiatives &amp; workshops</h2>
          <p className="text-gray-700 mb-4">We host and support wellbeing activities for the local community — from practitioner Q&amp;A sessions to educational botanical workshops. Recent and upcoming highlights:</p>
          <ul className="space-y-3">
            <li className="bg-white rounded-xl border border-border p-4 flex items-start gap-3"><Leaf className="w-5 h-5 text-emerald-700 mt-0.5" /><span className="text-gray-700"><strong>Herbalist Q&amp;A sessions</strong> — free discovery calls with Mrs. Varsha Soneji, scheduled throughout the month.</span></li>
            <li className="bg-white rounded-xl border border-border p-4 flex items-start gap-3"><Leaf className="w-5 h-5 text-emerald-700 mt-0.5" /><span className="text-gray-700"><strong>Botanical wellbeing workshops</strong> — educational sessions on curcumin and everyday natural nutrition (announced via our newsletter and Eventbrite).</span></li>
            <li className="bg-white rounded-xl border border-border p-4 flex items-start gap-3"><Leaf className="w-5 h-5 text-emerald-700 mt-0.5" /><span className="text-gray-700"><strong>Community events</strong> — as an Official Wellbeing Partner of local and diaspora events across the UK.</span></li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-emerald-900 mb-5 font-heading">Service summary</h2>
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm bg-white" data-testid="northampton-services-table">
              <thead className="bg-emerald-50 text-emerald-900">
                <tr><th className="text-left p-3 font-semibold">Service</th><th className="text-left p-3 font-semibold">Format</th><th className="text-left p-3 font-semibold">Who it's for</th></tr>
              </thead>
              <tbody className="divide-y divide-border text-gray-700">
                <tr><td className="p-3 font-medium">Free 15-min Discovery Call</td><td className="p-3">Online / phone</td><td className="p-3">Anyone new to natural wellbeing</td></tr>
                <tr><td className="p-3 font-medium">1-to-1 Herbal Consultation</td><td className="p-3">In person or video</td><td className="p-3">Personalised botanical guidance</td></tr>
                <tr><td className="p-3 font-medium">Wellbeing workshops</td><td className="p-3">In person / online</td><td className="p-3">Local community &amp; groups</td></tr>
                <tr><td className="p-3 font-medium">BCM-95® supplement supply</td><td className="p-3">UK-wide delivery</td><td className="p-3">Local &amp; national customers</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="bg-emerald-50 rounded-2xl border border-emerald-100 p-6 md:p-8 text-center">
          <h2 className="text-2xl font-bold text-emerald-900 mb-3 font-heading">Speak with our Medical Herbalist</h2>
          <Link to="/medical-herbalist-consultation" data-testid="northampton-cta" className="inline-flex items-center gap-2 bg-emerald-700 text-white font-bold px-7 py-3.5 rounded-full hover:bg-emerald-800 transition-colors">
            <CalendarCheck className="w-5 h-5" /> Book a consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </div>
  );
}
